'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';

import Icon from '@/components/ui/AppIcon';

import { diveSites, REGIONS, type DiveSite, type RegionId, type DiveType, type AccessType, type SkillLevel, type DepthBand } from '../diveSitesData';

// ─── Design tokens ────────────────────────────────────────────────────────────

const CYAN = '#6FD3E0';
const ORANGE = '#FF8A3D';
const BG_DARK = '#07111C';
const BG_CARD = '#0F2234';
const BG_SECTION = '#0E1C2B';
const TEXT_PRIMARY = '#EAF4F8';
const TEXT_SECONDARY = '#C8DDE6';
const BORDER = 'rgba(111,211,224,0.12)';

const inputStyle = {
  backgroundColor: '#0B1622',
  color: TEXT_PRIMARY,
  border: `1px solid ${BORDER}`,
};

// ─── Branded icon map ─────────────────────────────────────────────────────────

const TYPE_SVG_ICONS: Record<string, string> = {
  Wreck: 'AnchorIcon',
  Reef: 'SparklesIcon',
  Cave: 'MoonIcon',
  Wall: 'ArrowTrendingDownIcon',
  'Artificial Reef': 'CubeIcon',
  Shore: 'MapPinIcon',
  Technical: 'CogIcon',
};

// ─── Custom dive-type SVG icons ───────────────────────────────────────────────

function WreckIcon({ size = 32, color = 'currentColor', strokeWidth = 1.6 }: { size?: number; color?: string; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {/* Hull bottom */}
      <path d="M4 20 Q16 26 28 20" />
      {/* Hull sides */}
      <line x1="4" y1="20" x2="6" y2="14" />
      <line x1="28" y1="20" x2="26" y2="14" />
      {/* Deck */}
      <line x1="6" y1="14" x2="26" y2="14" />
      {/* Superstructure */}
      <rect x="11" y="9" width="10" height="5" rx="1" />
      {/* Funnel */}
      <rect x="14" y="5" width="4" height="4" rx="0.5" />
      {/* Porthole */}
      <circle cx="9" cy="17" r="1.2" />
      <circle cx="23" cy="17" r="1.2" />
      {/* Mast */}
      <line x1="16" y1="5" x2="16" y2="3" />
      <line x1="13" y1="4" x2="19" y2="4" />
    </svg>
  );
}

function ReefIcon({ size = 32, color = 'currentColor', strokeWidth = 1.6 }: { size?: number; color?: string; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {/* Central coral branch */}
      <line x1="16" y1="28" x2="16" y2="14" />
      <line x1="16" y1="20" x2="11" y2="14" />
      <line x1="16" y1="17" x2="21" y2="11" />
      <line x1="11" y1="14" x2="8" y2="10" />
      <line x1="11" y1="14" x2="13" y2="9" />
      <line x1="21" y1="11" x2="19" y2="7" />
      <line x1="21" y1="11" x2="24" y2="8" />
      <line x1="16" y1="14" x2="16" y2="9" />
      {/* Coral tips (small circles) */}
      <circle cx="8" cy="9.5" r="1.2" fill={color} stroke="none" />
      <circle cx="13" cy="8.5" r="1.2" fill={color} stroke="none" />
      <circle cx="16" cy="8.5" r="1.2" fill={color} stroke="none" />
      <circle cx="19" cy="6.5" r="1.2" fill={color} stroke="none" />
      <circle cx="24" cy="7.5" r="1.2" fill={color} stroke="none" />
      {/* Sea floor */}
      <line x1="4" y1="28" x2="28" y2="28" />
    </svg>
  );
}

function CaveIcon({ size = 32, color = 'currentColor', strokeWidth = 1.6 }: { size?: number; color?: string; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {/* Arch/tunnel opening */}
      <path d="M5 26 L5 14 Q5 6 16 6 Q27 6 27 14 L27 26" />
      {/* Inner arch (tunnel depth) */}
      <path d="M9 26 L9 15 Q9 10 16 10 Q23 10 23 15 L23 26" />
      {/* Light rays from inside */}
      <line x1="16" y1="10" x2="16" y2="14" strokeOpacity="0.5" />
      <line x1="13" y1="11" x2="12" y2="15" strokeOpacity="0.4" />
      <line x1="19" y1="11" x2="20" y2="15" strokeOpacity="0.4" />
      {/* Rock texture on sides */}
      <line x1="5" y1="18" x2="9" y2="18" />
      <line x1="23" y1="18" x2="27" y2="18" />
      <line x1="5" y1="22" x2="9" y2="22" />
      <line x1="23" y1="22" x2="27" y2="22" />
    </svg>
  );
}

function WallIcon({ size = 32, color = 'currentColor', strokeWidth = 1.6 }: { size?: number; color?: string; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {/* Cliff face — vertical wall */}
      <line x1="12" y1="4" x2="12" y2="28" />
      {/* Layered rock strata */}
      <line x1="12" y1="8" x2="5" y2="8" />
      <line x1="12" y1="13" x2="5" y2="13" />
      <line x1="12" y1="18" x2="5" y2="18" />
      <line x1="12" y1="23" x2="5" y2="23" />
      {/* Slight ledge overhangs */}
      <line x1="5" y1="8" x2="7" y2="10" />
      <line x1="5" y1="13" x2="8" y2="15" />
      <line x1="5" y1="18" x2="6" y2="20" />
      {/* Deep blue drop-off side — arrow indicating depth */}
      <line x1="22" y1="8" x2="22" y2="24" />
      <polyline points="19,21 22,25 25,21" />
      {/* Surface line */}
      <line x1="12" y1="4" x2="28" y2="4" />
    </svg>
  );
}

function ShoreIcon({ size = 32, color = 'currentColor', strokeWidth = 1.6 }: { size?: number; color?: string; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {/* Wave 1 */}
      <path d="M3 14 Q7 10 11 14 Q15 18 19 14 Q23 10 29 14" />
      {/* Wave 2 */}
      <path d="M3 19 Q7 15 11 19 Q15 23 19 19 Q23 15 29 19" />
      {/* Wave 3 (smaller, near shore) */}
      <path d="M3 24 Q8 21 13 24 Q18 27 23 24 Q26 22 29 24" strokeOpacity="0.5" />
      {/* Shore/beach line */}
      <line x1="3" y1="28" x2="29" y2="28" />
      {/* Entry point marker */}
      <circle cx="16" cy="28" r="2" />
      <line x1="16" y1="26" x2="16" y2="22" />
    </svg>
  );
}

function ArtificialReefIcon({ size = 32, color = 'currentColor', strokeWidth = 1.6 }: { size?: number; color?: string; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {/* Concrete module / reef ball base */}
      <path d="M8 24 L6 28 L26 28 L24 24 Z" />
      {/* Dome structure */}
      <path d="M8 24 Q8 14 16 12 Q24 14 24 24" />
      {/* Holes in structure */}
      <circle cx="12" cy="22" r="2" />
      <circle cx="20" cy="22" r="2" />
      <circle cx="16" cy="17" r="2" />
      {/* Marine growth — small coral sprigs on top */}
      <line x1="16" y1="12" x2="16" y2="8" />
      <line x1="16" y1="10" x2="13" y2="7" />
      <line x1="16" y1="10" x2="19" y2="7" />
      <circle cx="13" cy="6.5" r="1" fill={color} stroke="none" />
      <circle cx="19" cy="6.5" r="1" fill={color} stroke="none" />
      <circle cx="16" cy="7.5" r="1" fill={color} stroke="none" />
    </svg>
  );
}

function TechnicalIcon({ size = 32, color = 'currentColor', strokeWidth = 1.6 }: { size?: number; color?: string; strokeWidth?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {/* Diver body */}
      <ellipse cx="16" cy="18" rx="4" ry="6" />
      {/* Twin tanks on back */}
      <rect x="19" y="13" width="3" height="8" rx="1.5" />
      <rect x="22.5" y="13" width="3" height="8" rx="1.5" />
      {/* Tank valves */}
      <line x1="20.5" y1="13" x2="20.5" y2="11" />
      <line x1="24" y1="13" x2="24" y2="11" />
      <line x1="19.5" y1="11" x2="25.5" y2="11" />
      {/* Regulator hose */}
      <path d="M19 16 Q17 14 15 15" strokeDasharray="1.5 1" />
      {/* Mask/head */}
      <circle cx="16" cy="11" r="3" />
      <line x1="13.5" y1="11" x2="18.5" y2="11" strokeOpacity="0.6" />
      {/* Fins */}
      <path d="M12 22 Q9 24 8 27" />
      <path d="M20 22 Q23 24 24 27" />
      {/* Depth gauge */}
      <circle cx="10" cy="17" r="2.5" />
      <line x1="10" y1="17" x2="11.2" y2="15.8" />
    </svg>
  );
}

// Map dive type key → custom SVG component
const DIVE_TYPE_SVG_MAP: Record<string, React.FC<{ size?: number; color?: string; strokeWidth?: number }>> = {
  Wreck: WreckIcon,
  Reef: ReefIcon,
  Cave: CaveIcon,
  Wall: WallIcon,
  Shore: ShoreIcon,
  'Artificial Reef': ArtificialReefIcon,
  Technical: TechnicalIcon,
};

function DiveTypeIcon({ type, size = 32, color = 'currentColor', strokeWidth = 1.6 }: { type: string; size?: number; color?: string; strokeWidth?: number }) {
  const SvgIcon = DIVE_TYPE_SVG_MAP[type];
  if (SvgIcon) return <SvgIcon size={size} color={color} strokeWidth={strokeWidth} />;
  // Fallback to HeroIcon
  return <Icon name={TYPE_SVG_ICONS[type] ?? 'SparklesIcon'} size={size} style={{ color }} strokeWidth={strokeWidth} />;
}

// PNG branded icons replaced with SVG HeroIcons via TYPE_SVG_ICONS above

const TYPE_ACCENT: Record<string, string> = {
  Wreck: '#FF8A3D',
  Reef: '#6FD3E0',
  Cave: '#a78bfa',
  Wall: '#38bdf8',
  'Artificial Reef': '#4ade80',
  Shore: '#6FD3E0',
  Technical: '#f87171',
};

const TYPE_GRADIENTS: Record<string, string> = {
  Wreck: 'linear-gradient(135deg,#1a2a3a 0%,#0d1f30 100%)',
  Reef: 'linear-gradient(135deg,#0d2a2a 0%,#071c1c 100%)',
  Cave: 'linear-gradient(135deg,#1a1a2e 0%,#0d0d1f 100%)',
  Wall: 'linear-gradient(135deg,#1a2030 0%,#0d1525 100%)',
  'Artificial Reef': 'linear-gradient(135deg,#1a2a1a 0%,#0d1f0d 100%)',
  Shore: 'linear-gradient(135deg,#0d2030 0%,#071525 100%)',
  Technical: 'linear-gradient(135deg,#2a1a1a 0%,#1f0d0d 100%)',
};

const LEVEL_COLORS: Record<string, string> = {
  'Open Water': '#4ade80',
  Advanced: '#FF8A3D',
  Technical: '#f87171',
};

const ACCESS_ICONS: Record<string, string> = {
  Shore: 'MapPinIcon',
  Boat: 'AnchorIcon',
  'Shore or Boat': 'ArrowsRightLeftIcon',
};

// ─── Region chips data ────────────────────────────────────────────────────────

const REGION_CHIP_ICONS: Record<string, React.ReactNode> = {
  'ayia-napa-protaras': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
  ),
  larnaca: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
  ),
  limassol: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M2 12c1.5-3 4-4.5 6-4.5s4.5 1.5 6 1.5 4.5-1.5 6-4.5"/><path d="M2 17c1.5-3 4-4.5 6-4.5s4.5 1.5 6 1.5 4.5-1.5 6-4.5"/></svg>
  ),
  paphos: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 10v11M16 10v11M12 10v11"/></svg>
  ),
  'akamas-latchi': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M17 8C8 10 5.9 16.17 3.82 19.5c1.5 1 3.18 1.5 5.18 1.5 4 0 8-2 10-6 2-4 1-9-2-7z"/></svg>
  ),
};

const REGION_CHIPS = [
  { id: 'ayia-napa-protaras', label: 'Ayia Napa & Protaras' },
  { id: 'larnaca', label: 'Larnaca' },
  { id: 'limassol', label: 'Limassol' },
  { id: 'paphos', label: 'Paphos' },
  { id: 'akamas-latchi', label: 'Akamas & Latchi' },
] as const;

// ─── Region contextual content ────────────────────────────────────────────────

const REGION_DESCRIPTIONS: Record<string, string> = {
  'ayia-napa-protaras': 'Ayia Napa & Protaras offer some of the most accessible and visually rewarding diving in Cyprus. Crystal-clear water, sheltered bays and a mix of shore dives, sea caves and the MUSAN underwater sculpture museum make this region ideal for beginners and intermediate divers alike. Visibility regularly reaches 20–30m.',
  larnaca:
    'Larnaca is home to the MS Zenobia — consistently ranked among the top 10 wreck dives in the world. Beyond the Zenobia, the region offers reef diving, sea caves and a range of sites suitable for all certification levels. Visibility is excellent year-round, with warm water from May to November.',
  limassol:
    'Limassol is Cyprus\'s hub for advanced and technical diving. Jubilee Shoals offers dramatic wall and reef diving, while the region\'s artificial wrecks and deeper sites attract experienced divers seeking more challenging conditions. Shore access is available at several sites for recreational divers.',
  paphos:
    'Paphos blends history and underwater exploration — from ancient amphora caves to accessible shore dives along a dramatic coastline. The region suits a wide range of divers, with beginner-friendly bays alongside more challenging reef and wreck sites. Conditions are generally calm, particularly on the south-facing coast.',
  'akamas-latchi': 'Akamas & Latchi offer the most remote and unspoiled diving in Cyprus. Located within the protected Akamas National Park, this region features exceptional water clarity, untouched reef systems and dramatic limestone sea caves. Access is primarily by boat from Latchi, adding to the sense of genuine exploration.',
};

const REGION_TYPE_DESCRIPTIONS: Record<string, Record<string, string>> = {
  'ayia-napa-protaras': {
    Wreck: 'Wreck diving in Ayia Napa & Protaras includes deliberately sunk vessels and artificial structures that have become thriving marine habitats. Sites are generally accessible to Open Water divers and offer excellent visibility.',
    Reef: 'Reef diving in Ayia Napa & Protaras takes place across rocky limestone formations teeming with sea bream, damselfish and occasional sea turtles. Most sites are suitable for beginners and offer calm, clear conditions.',
    Cave: 'Cave and cavern diving in Ayia Napa & Protaras features dramatic sea caves carved into the limestone coastline. Cavern dives are accessible to Advanced Open Water divers, with natural light effects and sheltered marine life.',
    Wall: 'Wall diving in Ayia Napa & Protaras offers vertical drop-offs covered in sponges and encrusting organisms, with pelagic species patrolling the blue water alongside.',
    'Artificial Reef': 'The MUSAN Underwater Museum near Ayia Napa is one of Cyprus\'s most unique dive experiences — submerged sculptures that double as artificial reef habitat, accessible to Open Water divers.',
    Shore: 'Shore diving in Ayia Napa & Protaras is ideal for beginners. Green Bay is a classic example — shallow, sheltered, with underwater sculptures and frequent turtle sightings. No boat required.',
    Technical: 'Technical diving in Ayia Napa & Protaras targets deeper sections of wrecks and walls beyond recreational limits. Specialist certification and equipment are required.',
  },
  larnaca: {
    Wreck: 'Wreck diving in Larnaca centres on the legendary MS Zenobia — a 172m Swedish ferry that sank in 1980 and is consistently ranked among the top 10 wreck dives in the world. The HMS Cricket and other wrecks add further variety for all levels.',
    Reef: 'Reef diving in Larnaca offers rocky limestone formations rich in Mediterranean marine life — sea bream, grouper, octopus and moray eels. Visibility is excellent and most sites are accessible to Open Water divers.',
    Cave: 'Cave diving in Larnaca includes the Pirate Caves — dramatic cavern dives with natural light effects and sheltered marine life. Advanced Open Water certification is recommended.',
    Wall: 'Wall diving in Larnaca features vertical drop-offs alongside the Zenobia and other sites, where pelagic species including barracuda and amberjack patrol the blue water.',
    'Artificial Reef': 'Artificial reef diving in Larnaca includes deliberately sunk vessels that have become encrusted with marine life, offering accessible dives for recreational divers.',
    Shore: 'Shore diving in Larnaca provides easy access to reef systems and sandy bottoms. Several sites along the coast are suitable for Open Water divers and offer good visibility.',
    Technical: 'Technical diving in Larnaca focuses on the deeper sections of the MS Zenobia, where trimix and advanced gas management are required. The Zenobia\'s cargo holds and deeper superstructure attract experienced technical divers.',
  },
  limassol: {
    Wreck: 'Wreck diving in Limassol includes deliberately sunk vessels and artificial structures that have developed into rich marine habitats. Sites are accessible to recreational divers and offer good visibility.',
    Reef: 'Reef diving in Limassol features rocky formations and seagrass meadows supporting a diverse range of Mediterranean species. Conditions are generally calm on the south-facing coast.',
    Cave: 'Cave and cavern diving in Limassol offers dramatic swim-throughs and sheltered formations along the limestone coastline. Advanced certification is recommended.',
    Wall: 'Wall diving at Jubilee Shoals in Limassol is one of Cyprus\'s most dramatic underwater experiences — vertical rock faces dropping into deep blue water, covered in sponges and gorgonian fans.',
    'Artificial Reef': 'Artificial reef diving in Limassol includes purpose-built structures and sunk vessels that attract marine life and offer accessible dives for recreational divers.',
    Shore: 'Shore diving in Limassol provides access to reef systems and sandy bottoms along the south coast. Several sites are suitable for Open Water divers.',
    Technical: 'Technical diving in Limassol targets the deeper sections of Jubilee Shoals and other advanced sites beyond recreational limits. Specialist certification and equipment are required.',
  },
  paphos: {
    Wreck: 'Wreck diving in Paphos includes historic wrecks and deliberately sunk vessels that have become encrusted with marine life. Sites range from shallow recreational dives to deeper advanced wrecks.',
    Reef: 'Reef diving in Paphos takes place across rocky limestone formations along a dramatic coastline. Conditions are generally calm on the south-facing coast, with good visibility and diverse marine life.',
    Cave: 'Cave diving in Paphos includes the Amphora Cave — a dramatic cavern dive with natural light effects and historic artefacts. Advanced Open Water certification is recommended for most cave sites.',
    Wall: 'Wall diving in Paphos features vertical drop-offs along the dramatic limestone coastline, with sponges, gorgonian fans and pelagic species.',
    'Artificial Reef': 'Artificial reef diving in Paphos includes purpose-built structures and sunk vessels that attract marine life and offer accessible dives for recreational divers.',
    Shore: 'Shore diving in Paphos is accessible from several sheltered bays along the coast. Sites are suitable for Open Water divers and offer a mix of reef, sand and rocky formations.',
    Technical: 'Technical diving in Paphos targets deeper reef and wall sections beyond recreational limits. Specialist certification and equipment are required.',
  },
  'akamas-latchi': {
    Wreck: 'Wreck diving in Akamas & Latchi is limited but rewarding — remote sites accessed by boat from Latchi harbour, with excellent visibility and minimal diver traffic.',
    Reef: 'Reef diving in Akamas & Latchi takes place across untouched limestone formations within the protected Akamas National Park. Exceptional visibility — often exceeding 30m — and minimal disturbance make these some of the finest reef dives in Cyprus.',
    Cave: 'Cave diving in Akamas & Latchi features dramatic sea caves carved into the limestone coastline. Cavern dives are accessible to Advanced Open Water divers; full cave penetration requires specialist cave diving certification.',
    Wall: 'Wall diving at Cape Arnaoutis and other Akamas sites offers some of the most dramatic underwater scenery in Cyprus — vertical rock faces dropping into deep blue water, with pelagic species patrolling alongside.',
    'Artificial Reef': 'Artificial reef diving in Akamas & Latchi is limited, but natural reef systems within the protected marine area offer similar biodiversity and structure.',
    Shore: 'Shore diving in Akamas & Latchi is limited due to the remote coastline. Most sites are accessed by boat from Latchi harbour, though some sheltered coves offer shore entry for experienced divers.',
    Technical: 'Technical diving in Akamas & Latchi targets the deeper walls at Cape Arnaoutis and other advanced sites beyond recreational limits. The remote location and variable conditions require careful planning and specialist certification.',
  },
};

// ─── Dynamic header helpers ───────────────────────────────────────────────────

function getDynamicH1(region: string, diveType: string): string {
  const regionLabel = REGIONS.find((r) => r.id === region)?.label;
  if (region === 'all' && diveType === 'all') return 'All Dive Sites in Cyprus';
  if (region !== 'all' && diveType === 'all') return `Diving in ${regionLabel}, Cyprus`;
  if (region === 'all' && diveType !== 'all') return `${diveType} Diving in Cyprus`;
  return `${diveType} Diving in ${regionLabel}`;
}

function getDynamicSubheading(region: string, diveType: string): string {
  if (region === 'all' && diveType === 'all') {
    return 'Explore Cyprus dive sites by region, depth, access type and diving style — from beginner shore dives to world-class wrecks, reefs, caves and technical diving locations.';
  }
  const regionLabel = REGIONS.find((r) => r.id === region)?.label;
  if (region !== 'all' && diveType === 'all') {
    return `Discover the best scuba diving in ${regionLabel}, Cyprus — explore all dive sites, conditions and planning information for this region.`;
  }
  if (region === 'all' && diveType !== 'all') {
    return `Find the best ${diveType.toLowerCase()} diving sites across Cyprus — filter by region, skill level and access type to plan your dive.`;
  }
  return `Explore ${diveType.toLowerCase()} diving in ${regionLabel} — find sites, conditions and planning information for ${diveType.toLowerCase()} dives in this region.`;
}

function getContextualIntro(region: string, diveType: string): string | null {
  if (region === 'all') return null;
  if (diveType !== 'all' && REGION_TYPE_DESCRIPTIONS[region]?.[diveType]) {
    return REGION_TYPE_DESCRIPTIONS[region][diveType];
  }
  return REGION_DESCRIPTIONS[region] ?? null;
}

// ─── Dive Site Types data ─────────────────────────────────────────────────────

const DIVE_SITE_TYPES = [
  {
    key: 'Wreck',
    label: 'Wreck Diving',
    svgIcon: 'AnchorIcon',
    accent: '#FF8A3D',
    description: 'Sunken ships turned into thriving marine habitats.',
    detail: 'Wreck diving is one of Cyprus\'s greatest highlights. The MS Zenobia near Larnaca — a 172m Swedish ferry that sank in 1980 — is consistently ranked among the top 10 wreck dives in the world. Wrecks provide shelter for octopus, moray eels, grouper and schools of bream. Many are accessible to Open Water divers, while deeper sections require Advanced certification. Expect dramatic swim-throughs, cargo holds and encrusted superstructures.',
  },
  {
    key: 'Reef',
    label: 'Reef Diving',
    svgIcon: 'SparklesIcon',
    accent: '#6FD3E0',
    description: 'Natural rocky reefs rich in Mediterranean marine life.',
    detail: 'Cyprus reefs are rocky limestone formations rather than tropical coral, but they are alive with colour and biodiversity. Expect sea bream, damselfish, parrotfish, nudibranchs, starfish and occasional sea turtles. Seagrass meadows (Posidonia oceanica) are protected habitats and home to seahorses and pipefish. Most reef dives are suitable for Open Water divers and offer excellent visibility of 15–30m.',
  },
  {
    key: 'Cave',
    label: 'Cave & Cavern',
    svgIcon: 'MoonIcon',
    accent: '#a78bfa',
    description: 'Dramatic swim-throughs carved into the limestone coastline.',
    detail: 'Cyprus\'s limestone coastline has created a network of underwater caves, caverns and swim-throughs. Cavern diving (where daylight is always visible) is accessible to Advanced Open Water divers. True cave diving beyond the light zone requires specialist cave diving certification. Notable sites include the Pirate Caves in Larnaca and the Amphora Cave in Paphos. These sites offer dramatic light effects, unique geology and shelter for lobster and moray eels.',
  },
  {
    key: 'Wall',
    label: 'Wall & Drop-off',
    svgIcon: 'ArrowTrendingDownIcon',
    accent: '#38bdf8',
    description: 'Vertical walls plunging into the deep blue.',
    detail: 'Wall diving in Cyprus offers some of the most dramatic underwater scenery in the Mediterranean. Vertical rock faces drop from shallow reef tops into deep blue water, covered in sponges, gorgonian fans and encrusting organisms. Pelagic species such as barracuda and amberjack patrol the blue water alongside the walls. Jubilee Shoals in Limassol and sites around Akamas are prime examples. Advanced certification is recommended for deeper wall sections.',
  },
  {
    key: 'Shore',
    label: 'Shore Diving',
    svgIcon: 'MapPinIcon',
    accent: '#6FD3E0',
    description: 'Easy shore-entry dives from calm, sheltered bays.',
    detail: 'Shore diving is ideal for beginners and those who prefer flexibility without a boat. Cyprus has numerous sheltered bays with sandy bottoms, gentle reefs and calm conditions — particularly on the south and east coasts. Green Bay in Protaras is a classic example, with shallow water, underwater sculptures and frequent turtle sightings. Shore dives are typically Open Water level and offer the freedom to dive at your own pace.',
  },
  {
    key: 'Artificial Reef',
    label: 'Artificial Reef',
    svgIcon: 'CubeIcon',
    accent: '#4ade80',
    description: 'Purpose-built structures colonised by marine life.',
    detail: 'Artificial reefs in Cyprus include deliberately sunk vessels, concrete structures and purpose-built modules designed to attract marine life and relieve pressure on natural reefs. Over time these structures become encrusted with sponges, corals and algae, attracting fish, octopus and invertebrates. They are often placed at recreational depths and are accessible to Open Water divers. The MUSAN Underwater Museum near Ayia Napa features submerged sculptures that double as artificial reef habitat.',
  },
  {
    key: 'Technical',
    label: 'Technical Diving',
    svgIcon: 'CogIcon',
    accent: '#f59e0b',
    description: 'Advanced deep dives beyond recreational limits.',
    detail: 'Technical diving in Cyprus goes beyond the 40m recreational limit, requiring specialist training, equipment and gas mixtures such as trimix or nitrox. Sites like the deeper sections of MS Zenobia, Jubilee Shoals and certain Akamas walls attract experienced technical divers. Certification from recognised agencies (TDI, PADI TecRec, IANTD) is mandatory. Cyprus\'s excellent visibility and warm water make it a rewarding destination for technical divers seeking deep exploration.',
  },
];

// ─── Region meta ──────────────────────────────────────────────────────────────

const REGION_HIGHLIGHTS: Record<RegionId, string[]> = {
  'ayia-napa-protaras': ['Shore dives', 'Sea caves', 'Underwater sculptures', 'Wrecks'],
  larnaca: ['MS Zenobia wreck', 'HMS Cricket', 'Pirate caves', 'Reef diving'],
  limassol: ['Jubilee Shoals', 'Artificial wrecks', 'Technical diving', 'Wall diving'],
  paphos: ['Historic wrecks', 'Amphora cave', 'Shore dives', 'Beginner sites'],
  'akamas-latchi': ['Wall diving', 'Exceptional visibility', 'Marine life', 'Technical sites'],
};

// ─── Quick filter shortcuts ───────────────────────────────────────────────────

const QUICK_FILTERS = [
  { label: 'Wreck Diving', field: 'diveType', value: 'Wreck', icon: 'AnchorIcon', accent: '#FF8A3D' },
  { label: 'Beginner', field: 'skillLevel', value: 'Open Water', icon: 'StarIcon', accent: '#4ade80' },
  { label: 'Shore Entry', field: 'access', value: 'Shore', icon: 'MapPinIcon', accent: CYAN },
  { label: 'Deep (35m+)', field: 'depth', value: 'Deep', icon: 'ArrowTrendingDownIcon', accent: '#a78bfa' },
  { label: 'Cave Diving', field: 'diveType', value: 'Cave', icon: 'MoonIcon', accent: '#a78bfa' },
  { label: 'Reef Diving', field: 'diveType', value: 'Reef', icon: 'SparklesIcon', accent: CYAN },
];

// ─── Filter state ─────────────────────────────────────────────────────────────

interface FilterState {
  region: string;
  diveType: string;
  access: string;
  skillLevel: string;
  depth: string;
}

const DEFAULT_FILTERS: FilterState = {
  region: 'all',
  diveType: 'all',
  access: 'all',
  skillLevel: 'all',
  depth: 'all',
};

const FILTER_LABELS: Record<keyof FilterState, Record<string, string>> = {
  region: Object.fromEntries(REGIONS.map((r) => [r.id, r.label])),
  diveType: { Wreck: 'Wreck', Reef: 'Reef', Cave: 'Cave', Wall: 'Wall', 'Artificial Reef': 'Artificial Reef', Shore: 'Shore' },
  access: { Shore: 'Shore Access', Boat: 'Boat Access', 'Shore or Boat': 'Shore or Boat' },
  skillLevel: { 'Open Water': 'Open Water', Advanced: 'Advanced', Technical: 'Technical' },
  depth: { Shallow: 'Shallow \u226418m', Medium: 'Medium 19\u201335m', Deep: 'Deep 35m+' },
};

// ─── Dive Site Card ───────────────────────────────────────────────────────────

function SiteIconCard({ site }: { site: DiveSite }) {
  const primaryType = site.diveType[0];
  const accent = TYPE_ACCENT[primaryType] ?? CYAN;
  const levelColor = LEVEL_COLORS[site.minLevel] ?? CYAN;
  const allTypes = site.diveType;

  return (
    <div
      style={{ backgroundColor: BG_CARD, border: `1px solid ${BORDER}` }}
      className="rounded-2xl overflow-hidden shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group flex flex-col"
    >
      {/* ── TOP ROW: Icons (left) + Region tag (right) ── */}
      <div
        className="flex items-center justify-between px-5 pt-5 pb-4"
        style={{ borderBottom: `1px solid ${BORDER}` }}
      >
        {/* All dive type icons side by side */}
        <div className="flex items-center gap-2">
          {allTypes.map((type) => {
            const typeAccent = TYPE_ACCENT[type] ?? CYAN;
            const iconSize = allTypes.length > 2 ? 44 : allTypes.length > 1 ? 52 : 64;
            const svgSize = allTypes.length > 2 ? 22 : allTypes.length > 1 ? 26 : 32;
            return (
              <div
                key={type}
                style={{
                  width: iconSize,
                  height: iconSize,
                  borderRadius: 12,
                  backgroundColor: `${typeAccent}18`,
                  border: `1px solid ${typeAccent}40`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 0 16px ${typeAccent}22`,
                  flexShrink: 0,
                }}
              >
                <DiveTypeIcon type={type} size={svgSize} color={typeAccent} strokeWidth={1.8} />
              </div>
            );
          })}
        </div>

        {/* Region tag */}
        <span
          style={{
            backgroundColor: `${ORANGE}18`,
            color: ORANGE,
            border: `1px solid ${ORANGE}35`,
            fontSize: 10,
            fontWeight: 700,
            padding: '3px 9px',
            borderRadius: 4,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            whiteSpace: 'nowrap',
          }}
        >
          {site.regionLabel}
        </span>
      </div>

      {/* ── Body ── */}
      <div className="px-5 pt-4 pb-5 flex flex-col flex-1">

        {/* Title */}
        <h3
          style={{ color: TEXT_PRIMARY }}
          className="text-lg font-headline font-bold mb-3 group-hover:text-cyan-300 transition-colors leading-snug"
        >
          {site.name}
        </h3>

        {/* Meta line: Access | Skill Level */}
        <div
          className="flex flex-wrap gap-x-5 gap-y-1.5 mb-4 pb-4"
          style={{ borderBottom: `1px solid ${BORDER}` }}
        >
          <span style={{ color: TEXT_SECONDARY, fontSize: 12 }} className="flex items-center gap-1.5">
            <Icon name={ACCESS_ICONS[site.access] ?? 'MapPinIcon'} size={13} style={{ color: CYAN }} />
            <span style={{ color: TEXT_SECONDARY, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Access:</span>
            &nbsp;{site.access}
          </span>
          <span
            style={{ color: levelColor, fontSize: 12, fontWeight: 600 }}
            className="flex items-center gap-1.5"
            title={`Certification level required: ${site.minLevel}${site.minLevel === 'Technical' ? ' (e.g. TDI, PADI TecRec or equivalent)' : site.minLevel === 'Advanced' ? ' (e.g. PADI Advanced Open Water or equivalent)' : ' (e.g. PADI Open Water or equivalent)'}`}
          >
            <Icon name="AcademicCapIcon" size={13} style={{ color: levelColor }} />
            <span style={{ color: TEXT_SECONDARY, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Level:</span>
            &nbsp;{site.minLevel}
            <span style={{ fontSize: 10, opacity: 0.65, fontWeight: 400 }}>cert.</span>
          </span>
        </div>

        {/* Description */}
        <p style={{ color: TEXT_SECONDARY, fontSize: 13 }} className="mb-4 leading-relaxed line-clamp-3">
          {site.description}
        </p>

        {/* Tags row — ALL applicable types */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {allTypes.map((t) => {
            const tagAccent = TYPE_ACCENT[t] ?? CYAN;
            return (
              <span
                key={t}
                style={{
                  backgroundColor: `${tagAccent}15`,
                  color: tagAccent,
                  border: `1px solid ${tagAccent}35`,
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '3px 9px',
                  borderRadius: 5,
                }}
              >
                {t}
              </span>
            );
          })}
        </div>

        {/* Key Data Panel */}
        <div
          style={{ backgroundColor: 'rgba(111,211,224,0.04)', border: `1px solid ${BORDER}` }}
          className="rounded-xl p-3 mb-4 grid grid-cols-2 gap-3"
        >
          <div className="flex items-center gap-2">
            <Icon name="ArrowTrendingDownIcon" size={14} style={{ color: CYAN }} />
            <div>
              <div style={{ color: TEXT_SECONDARY, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Max Depth</div>
              <div style={{ color: TEXT_PRIMARY, fontSize: 13, fontWeight: 700 }}>{site.maxDepth}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="EyeIcon" size={14} style={{ color: CYAN }} />
            <div>
              <div style={{ color: TEXT_SECONDARY, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Visibility</div>
              <div style={{ color: TEXT_PRIMARY, fontSize: 13, fontWeight: 700 }}>{site.visibility}</div>
            </div>
          </div>
        </div>

        {/* GPS Coordinates */}
        {site.gps && (
          <div className="flex items-start gap-2 mb-4">
            <Icon name="MapPinIcon" size={12} style={{ color: TEXT_SECONDARY, marginTop: 1, flexShrink: 0, opacity: 0.6 }} />
            <span style={{ color: TEXT_SECONDARY, fontSize: 11, fontFamily: 'monospace', opacity: 0.7 }}>{site.gps}</span>
          </div>
        )}

        {/* Marine Life — clickable tags linking to marine life page */}
        {site.marineLife && site.marineLife.length > 0 && (
          <div className="mb-4 pb-4" style={{ borderBottom: `1px solid ${BORDER}` }}>
            <p style={{ color: TEXT_PRIMARY, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }} className="mb-2 flex items-center gap-1.5">
              <Icon name="SparklesIcon" size={11} style={{ color: CYAN }} />
              Marine Life
            </p>
            <div className="flex flex-wrap gap-1.5">
              {site.marineLife.slice(0, 5).map((species) => (
                <Link
                  key={species}
                  href={`/marine-life?search=${encodeURIComponent(species)}`}
                  style={{
                    backgroundColor: 'rgba(111,211,224,0.07)',
                    color: CYAN,
                    border: `1px solid rgba(111,211,224,0.20)`,
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '3px 8px',
                    borderRadius: 5,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    textDecoration: 'none',
                    transition: 'background 0.15s',
                  }}
                  className="hover:bg-cyan-400/20"
                >
                  <Icon name="ArrowTopRightOnSquareIcon" size={9} style={{ color: CYAN, opacity: 0.7 }} />
                  {species}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Highlights */}
        {site.highlights && site.highlights.length > 0 && (
          <div>
            <p style={{ color: TEXT_PRIMARY, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }} className="mb-2">
              Highlights
            </p>
            <ul className="space-y-1.5">
              {site.highlights.slice(0, 4).map((h, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span style={{ color: accent, fontSize: 10, marginTop: 3, flexShrink: 0 }}>▸</span>
                  <span style={{ color: TEXT_SECONDARY, fontSize: 12, lineHeight: 1.5 }}>{h}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Active Filter Chip ───────────────────────────────────────────────────────

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span
      style={{
        backgroundColor: `${CYAN}18`,
        color: CYAN,
        border: `1px solid ${CYAN}40`,
        fontSize: 12,
        fontWeight: 600,
        padding: '4px 10px',
        borderRadius: 20,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {label}
      <button
        onClick={onRemove}
        style={{ color: CYAN, lineHeight: 1, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        aria-label={`Remove ${label} filter`}
      >
        ✕
      </button>
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const DiveSitesMasterInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [filtered, setFiltered] = useState<DiveSite[]>([]);
  const [expandedType, setExpandedType] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const applyFilters = useCallback(() => {
    let result = diveSites;
    if (filters.region !== 'all') result = result.filter((s) => s.region === filters.region);
    if (filters.diveType !== 'all') result = result.filter((s) => s.diveType.includes(filters.diveType as DiveType));
    if (filters.access !== 'all') result = result.filter((s) => s.access === filters.access);
    if (filters.skillLevel !== 'all') result = result.filter((s) => s.minLevel === filters.skillLevel);
    if (filters.depth !== 'all') result = result.filter((s) => s.depthBand === filters.depth);
    setFiltered(result);
  }, [filters]);

  useEffect(() => {
    if (isHydrated) applyFilters();
  }, [filters, isHydrated, applyFilters]);

  const handleReset = () => setFilters(DEFAULT_FILTERS);

  const removeFilter = (key: keyof FilterState) => setFilters((p) => ({ ...p, [key]: 'all' }));

  const activeFilters = (Object.entries(filters) as [keyof FilterState, string][]).filter(([, v]) => v !== 'all');

  // Dynamic content
  const dynamicH1 = getDynamicH1(filters.region, filters.diveType);
  const dynamicSubheading = getDynamicSubheading(filters.region, filters.diveType);
  const contextualIntro = getContextualIntro(filters.region, filters.diveType);
  const selectedRegionLabel = filters.region !== 'all' ? REGIONS.find((r) => r.id === filters.region)?.label : null;

  // Results label
  const getResultsLabel = () => {
    if (filters.region !== 'all') {
      return `Showing ${filtered.length} dive site${filtered.length !== 1 ? 's' : ''} in ${selectedRegionLabel}`;
    }
    const typeName = filters.diveType !== 'all' ? filters.diveType : null;
    const levelName = filters.skillLevel !== 'all' ? filters.skillLevel : null;
    if (!typeName && !levelName) {
      return `Showing all ${filtered.length} dive sites in Cyprus — including wreck, reef, cave, wall and shore diving locations.`;
    }
    const parts: string[] = [];
    if (typeName) parts.push(`${typeName.toLowerCase()} diving`);
    if (levelName) parts.push(`${levelName} level`);
    const typeStr = parts.length > 0 ? parts.join(', ') + ' ' : '';
    return `Showing ${filtered.length} ${typeStr}dive site${filtered.length !== 1 ? 's' : ''} in Cyprus.`;
  };

  // Dive type guide title
  const getDiveTypeGuideTitle = (typeKey: string) => {
    const typeLabel = DIVE_SITE_TYPES.find((t) => t.key === typeKey)?.label ?? typeKey;
    if (selectedRegionLabel) {
      return `${typeKey} Diving in ${selectedRegionLabel}`;
    }
    return typeLabel;
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: BG_DARK }}>
        <div className="container mx-auto px-4 py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-12 rounded w-1/2" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-96 rounded-2xl" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: BG_DARK }}>

      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <section
        style={{ background: 'linear-gradient(160deg,#0E1C2B 0%,#07111C 55%,#0A1A2E 100%)' }}
        className="py-16 md:py-24 relative overflow-hidden"
      >
        <div
          className="absolute top-0 right-0 w-96 h-96 opacity-5 pointer-events-none"
          style={{ background: `radial-gradient(circle, ${CYAN} 0%, transparent 70%)` }}
        />
        <div className="container mx-auto px-4 relative">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm mb-8" aria-label="Breadcrumb">
            <Link href="/" style={{ color: CYAN }} className="hover:underline">Home</Link>
            <span style={{ color: '#4A7A8A' }}>/</span>
            <span style={{ color: TEXT_PRIMARY }}>Dive Sites</span>
          </nav>

          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-4">
              <span
                style={{
                  backgroundColor: `${CYAN}18`,
                  color: CYAN,
                  border: `1px solid ${CYAN}35`,
                  fontSize: 12,
                  fontWeight: 700,
                  padding: '4px 12px',
                  borderRadius: 20,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Cyprus Dive Sites
              </span>
            </div>

            {/* Dynamic H1 */}
            <h1 style={{ color: TEXT_PRIMARY }} className="text-4xl md:text-5xl lg:text-6xl font-headline font-bold mb-5 leading-tight">
              {filters.region === 'all' && filters.diveType === 'all' ? (
                <>All Dive Sites<br /><span style={{ color: CYAN }}>in Cyprus</span></>
              ) : filters.region !== 'all' && filters.diveType === 'all' ? (
                <>Diving in <span style={{ color: CYAN }}>{selectedRegionLabel}</span>,<br />Cyprus</>
              ) : filters.region === 'all' && filters.diveType !== 'all' ? (
                <><span style={{ color: CYAN }}>{filters.diveType}</span> Diving<br />in Cyprus</>
              ) : (
                <><span style={{ color: CYAN }}>{filters.diveType}</span> Diving<br />in {selectedRegionLabel}</>
              )}
            </h1>

            {/* Dynamic subheading */}
            <p style={{ color: TEXT_SECONDARY }} className="text-lg md:text-xl mb-6 max-w-3xl leading-relaxed">
              {dynamicSubheading}
            </p>

            {/* Contextual intro block */}
            {contextualIntro && (
              <div
                style={{
                  backgroundColor: 'rgba(111,211,224,0.05)',
                  border: `1px solid ${BORDER}`,
                  borderLeft: `3px solid ${CYAN}`,
                  borderRadius: 10,
                  padding: '14px 18px',
                  marginBottom: 24,
                }}
              >
                <p style={{ color: TEXT_SECONDARY, fontSize: 14, lineHeight: 1.75 }}>
                  {contextualIntro}
                </p>
              </div>
            )}

            {/* ── REGION CHIPS ── */}
            <div className="mb-8">
              <p style={{ color: TEXT_SECONDARY, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em' }} className="mb-3">
                Browse by Region
              </p>
              <div className="flex flex-wrap gap-2">
                {REGION_CHIPS.map((chip) => {
                  const isActive = filters.region === chip.id;
                  return (
                    <button
                      key={chip.id}
                      onClick={() => {
                        if (isActive) {
                          setFilters((p) => ({ ...p, region: 'all' }));
                        } else {
                          setFilters((p) => ({ ...p, region: chip.id }));
                          document.getElementById('dive-sites-grid')?.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      style={{
                        backgroundColor: isActive ? `${CYAN}22` : `${CYAN}0C`,
                        color: isActive ? CYAN : TEXT_SECONDARY,
                        border: `1.5px solid ${isActive ? CYAN + '70' : BORDER}`,
                        fontSize: 13,
                        fontWeight: isActive ? 700 : 600,
                        padding: '8px 16px',
                        borderRadius: 24,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 7,
                        transition: 'all 0.15s',
                        boxShadow: isActive ? `0 0 14px ${CYAN}25` : 'none',
                      }}
                      className="hover:border-cyan-400/50 hover:text-cyan-300 transition-all"
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>{REGION_CHIP_ICONS[chip.id]}</span>
                      {chip.label}
                      {isActive && (
                        <span
                          style={{
                            backgroundColor: CYAN,
                            color: BG_DARK,
                            fontSize: 9,
                            fontWeight: 800,
                            padding: '1px 6px',
                            borderRadius: 10,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          Active
                        </span>
                      )}
                    </button>
                  );
                })}
                {filters.region !== 'all' && (
                  <button
                    onClick={() => setFilters((p) => ({ ...p, region: 'all' }))}
                    style={{
                      backgroundColor: 'transparent',
                      color: TEXT_SECONDARY,
                      border: `1px solid rgba(111,211,224,0.18)`,
                      fontSize: 12,
                      fontWeight: 600,
                      padding: '8px 14px',
                      borderRadius: 24,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                    }}
                    className="hover:opacity-80 transition-opacity"
                  >
                    <Icon name="ArrowPathIcon" size={12} />
                    All Regions
                  </button>
                )}
              </div>
            </div>

            {/* Hero stat badges */}
            <div className="flex flex-wrap gap-3 mb-10">
              {[
                { icon: 'MapPinIcon', label: '5 Regions', color: CYAN },
                { icon: 'SparklesIcon', label: '40+ Dive Sites', color: CYAN },
                { icon: 'AnchorIcon', label: 'Wreck, Reef, Cave & Wall Diving', color: ORANGE },
              ].map(({ icon, label, color }) => (
                <div
                  key={label}
                  style={{ backgroundColor: `${color}10`, border: `1px solid ${color}28` }}
                  className="flex items-center space-x-2 px-4 py-2 rounded-full"
                >
                  <Icon name={icon} size={16} style={{ color }} />
                  <span style={{ color: TEXT_PRIMARY, fontSize: 14, fontWeight: 600 }}>{label}</span>
                </div>
              ))}
            </div>

            {/* Quick filter shortcuts */}
            <div>
              <p style={{ color: TEXT_SECONDARY, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em' }} className="mb-3">
                Quick Filters
              </p>
              <div className="flex flex-wrap gap-2">
                {QUICK_FILTERS.map((qf) => {
                  const isActive = filters[qf.field as keyof FilterState] === qf.value;
                  return (
                    <button
                      key={qf.label}
                      onClick={() => {
                        if (isActive) {
                          setFilters((p) => ({ ...p, [qf.field]: 'all' }));
                        } else {
                          setFilters((p) => ({ ...p, [qf.field]: qf.value }));
                          document.getElementById('dive-sites-grid')?.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      style={{
                        backgroundColor: isActive ? `${qf.accent}28` : `${qf.accent}10`,
                        color: qf.accent,
                        border: `1px solid ${isActive ? qf.accent + '60' : qf.accent + '30'}`,
                        fontSize: 13,
                        fontWeight: 600,
                        padding: '6px 14px',
                        borderRadius: 20,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        transition: 'all 0.15s',
                      }}
                    >
                      <Icon name={qf.icon} size={13} style={{ color: qf.accent }} />
                      {qf.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FILTER SECTION — directly below hero ──────────────────────────── */}
      <section
        id="dive-sites-filter"
        style={{ backgroundColor: BG_SECTION, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}
        className="py-8"
      >
        <div className="container mx-auto px-4">
          <div
            style={{ backgroundColor: BG_CARD, border: `1px solid ${BORDER}` }}
            className="rounded-2xl p-5 shadow-md"
          >
            {/* Filter header */}
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Icon name="FunnelIcon" size={17} style={{ color: CYAN }} />
                <span style={{ color: TEXT_PRIMARY, fontSize: 15, fontWeight: 700 }}>Filter Dive Sites</span>
                {activeFilters.length > 0 && (
                  <span
                    style={{
                      backgroundColor: CYAN,
                      color: BG_DARK,
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '1px 7px',
                      borderRadius: 10,
                    }}
                  >
                    {activeFilters.length}
                  </span>
                )}
              </div>
              {activeFilters.length > 0 && (
                <button
                  onClick={handleReset}
                  style={{ color: TEXT_SECONDARY, fontSize: 13, background: 'none', border: 'none', cursor: 'pointer' }}
                  className="flex items-center gap-1 hover:opacity-80 transition-opacity"
                >
                  <Icon name="ArrowPathIcon" size={13} />
                  Reset all
                </button>
              )}
            </div>

            {/* Helper text */}
            <p style={{ color: TEXT_SECONDARY, fontSize: 12, marginBottom: 16 }}>
              Use filters to find the best dive sites in Cyprus based on your experience level and diving preferences.
            </p>

            {/* Row 1: Region (primary) */}
            <div className="mb-3">
              <label style={{ color: CYAN, fontSize: 11, fontWeight: 700 }} className="block mb-1.5 uppercase tracking-wide flex items-center gap-1.5">
                <Icon name="MapPinIcon" size={11} style={{ color: CYAN }} />
                Region
                <span style={{ color: TEXT_SECONDARY, fontSize: 10, fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>— start here</span>
              </label>
              <select
                value={filters.region}
                onChange={(e) => setFilters((p) => ({ ...p, region: e.target.value }))}
                style={{ ...inputStyle, border: `1.5px solid ${filters.region !== 'all' ? CYAN + '60' : BORDER}` }}
                className="w-full px-3 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
              >
                <option value="all">All Regions</option>
                {REGIONS.map((r) => (
                  <option key={r.id} value={r.id}>{r.label}</option>
                ))}
              </select>
            </div>

            {/* Row 2: Dive Type */}
            <div className="mb-3">
              <label style={{ color: TEXT_SECONDARY, fontSize: 11, fontWeight: 700 }} className="block mb-1.5 uppercase tracking-wide">
                Dive Type
              </label>
              <select
                value={filters.diveType}
                onChange={(e) => setFilters((p) => ({ ...p, diveType: e.target.value }))}
                style={inputStyle}
                className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
              >
                <option value="all">All Types</option>
                <option value="Wreck">Wreck</option>
                <option value="Reef">Reef</option>
                <option value="Cave">Cave</option>
                <option value="Wall">Wall</option>
                <option value="Artificial Reef">Artificial Reef</option>
                <option value="Shore">Shore</option>
                <option value="Technical">Technical</option>
              </select>
            </div>

            {/* Row 3: Skill Level + Access */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label style={{ color: TEXT_SECONDARY, fontSize: 11, fontWeight: 700 }} className="block mb-1.5 uppercase tracking-wide">
                  Skill Level (Certification)
                </label>
                <select
                  value={filters.skillLevel}
                  onChange={(e) => setFilters((p) => ({ ...p, skillLevel: e.target.value }))}
                  style={inputStyle}
                  className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
                >
                  <option value="all">All Levels</option>
                  <option value="Open Water">Beginner (Open Water cert.)</option>
                  <option value="Advanced">Intermediate (Advanced cert.)</option>
                  <option value="Technical">Technical (Tech cert.)</option>
                </select>
              </div>
              <div>
                <label style={{ color: TEXT_SECONDARY, fontSize: 11, fontWeight: 700 }} className="block mb-1.5 uppercase tracking-wide">
                  Access
                </label>
                <select
                  value={filters.access}
                  onChange={(e) => setFilters((p) => ({ ...p, access: e.target.value }))}
                  style={inputStyle}
                  className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
                >
                  <option value="all">Shore or Boat</option>
                  <option value="Shore">Shore Only</option>
                  <option value="Boat">Boat Only</option>
                  <option value="Shore or Boat">Shore or Boat</option>
                </select>
              </div>
            </div>

            {/* Row 4: Depth */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label style={{ color: TEXT_SECONDARY, fontSize: 11, fontWeight: 700 }} className="block mb-1.5 uppercase tracking-wide">
                  Depth
                </label>
                <select
                  value={filters.depth}
                  onChange={(e) => setFilters((p) => ({ ...p, depth: e.target.value }))}
                  style={inputStyle}
                  className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
                >
                  <option value="all">All Depths</option>
                  <option value="Shallow">Shallow (\u226418m)</option>
                  <option value="Medium">Medium (19\u201335m)</option>
                  <option value="Deep">Deep (35m+)</option>
                </select>
              </div>
            </div>

            {/* Active filter chips */}
            {activeFilters.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-3" style={{ borderTop: `1px solid ${BORDER}` }}>
                {activeFilters.map(([key, value]) => (
                  <FilterChip
                    key={key}
                    label={FILTER_LABELS[key]?.[value] ?? value}
                    onRemove={() => removeFilter(key)}
                  />
                ))}
              </div>
            )}

            {/* Disclaimer */}
            <p
              style={{
                color: '#8AABB8',
                fontSize: 12,
                backgroundColor: 'rgba(111,211,224,0.04)',
                border: `1px solid rgba(111,211,224,0.10)`,
                borderRadius: 8,
                padding: '8px 12px',
                marginTop: 14,
              }}
              className="leading-relaxed"
            >
              <Icon name="InformationCircleIcon" size={13} style={{ color: CYAN, display: 'inline', marginRight: 5, verticalAlign: 'middle' }} />
              Dive conditions, access and required qualifications can vary. Always confirm details with a qualified local dive centre before diving.
            </p>
          </div>
        </div>
      </section>

      {/* ── DIVE SITE TYPES EXPLAINED — compact horizontal grid above listings ── */}
      <section
        id="dive-site-types"
        style={{ backgroundColor: BG_DARK, borderBottom: `1px solid ${BORDER}` }}
        className="py-14 md:py-16"
      >
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-3">
            <div>
              <span
                style={{
                  backgroundColor: `${CYAN}18`,
                  color: CYAN,
                  border: `1px solid ${CYAN}35`,
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '3px 10px',
                  borderRadius: 20,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  display: 'inline-block',
                  marginBottom: 10,
                }}
              >
                Dive Type
              </span>
              <h2 style={{ color: TEXT_PRIMARY }} className="text-2xl md:text-3xl font-headline font-bold mb-2">
                Dive Site Types Explained
              </h2>
              <p style={{ color: TEXT_SECONDARY, fontSize: 13 }} className="max-w-2xl leading-relaxed">
                Filter dive sites by type. Difficulty and certification requirements vary by site and are shown in individual listings.
              </p>
            </div>
            <button
              onClick={() => document.getElementById('dive-sites-grid')?.scrollIntoView({ behavior: 'smooth' })}
              style={{
                backgroundColor: `${CYAN}15`,
                color: CYAN,
                border: `1px solid ${CYAN}40`,
                fontSize: 13,
                fontWeight: 600,
                padding: '8px 18px',
                borderRadius: 20,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
              className="hover:opacity-80 transition-opacity"
            >
              <Icon name="FunnelIcon" size={13} style={{ color: CYAN }} />
              Filter dive sites by type ↓
            </button>
          </div>

          {/* Compact horizontal scroll on mobile, 7-col grid on desktop */}
          <div
            className="flex gap-3 overflow-x-auto pb-3 lg:grid lg:grid-cols-7 lg:overflow-visible lg:gap-3"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {DIVE_SITE_TYPES.map((type) => {
              const isSelected = filters.diveType === type.key;
              const isExpanded = expandedType === type.key;
              return (
                <button
                  key={type.key}
                  onClick={() => {
                    setExpandedType((prev) => (prev === type.key ? null : type.key));
                    setFilters((p) => ({ ...p, diveType: isSelected ? 'all' : type.key }));
                  }}
                  style={{
                    backgroundColor: isSelected ? `${type.accent}20` : BG_CARD,
                    border: isSelected
                      ? `2px solid ${type.accent}80`
                      : isExpanded
                      ? `1px solid ${type.accent}50`
                      : `1px solid rgba(111,211,224,0.14)`,
                    borderRadius: 16,
                    padding: '16px 10px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    minWidth: 130,
                    flexShrink: 0,
                    scrollSnapAlign: 'start',
                    boxShadow: isSelected
                      ? `0 0 18px ${type.accent}30, 0 4px 16px rgba(0,0,0,0.3)`
                      : '0 2px 8px rgba(0,0,0,0.2)',
                  }}
                  className="lg:min-w-0 lg:max-w-none hover:border-cyan-400/40 hover:shadow-lg hover:-translate-y-0.5 group flex flex-col items-center gap-2"
                  aria-expanded={isExpanded}
                >
                  <div
                    style={{
                      width: 90,
                      height: 90,
                      borderRadius: 18,
                      backgroundColor: isSelected ? `${type.accent}22` : `${type.accent}12`,
                      border: isSelected
                        ? `2px solid ${type.accent}60`
                        : `1px solid ${type.accent}28`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: isSelected
                        ? `0 0 28px ${type.accent}40`
                        : `0 0 18px ${type.accent}18`,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <DiveTypeIcon type={type.key} size={48} color={type.accent} strokeWidth={1.6} />
                  </div>
                  <span
                    style={{
                      color: isSelected ? type.accent : TEXT_PRIMARY,
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1.3,
                      transition: 'color 0.2s',
                      display: 'block',
                    }}
                    className="group-hover:text-cyan-300"
                  >
                    {type.label}
                  </span>
                  <span
                    style={{ color: TEXT_SECONDARY, fontSize: 10.5, lineHeight: 1.35, display: 'block' }}
                  >
                    {type.description}
                  </span>
                  {isSelected && (
                    <span
                      style={{
                        backgroundColor: `${type.accent}25`,
                        color: type.accent,
                        border: `1px solid ${type.accent}50`,
                        fontSize: 9,
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: 20,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        display: 'inline-block',
                      }}
                    >
                      Active
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* ── Expanded explanation panel ── */}
          {expandedType && (() => {
            const activeType = DIVE_SITE_TYPES.find((t) => t.key === expandedType);
            if (!activeType) return null;
            const guideTitle = getDiveTypeGuideTitle(activeType.key);
            return (
              <div
                style={{
                  backgroundColor: `${activeType.accent}0D`,
                  border: `1px solid ${activeType.accent}35`,
                  borderRadius: 16,
                  padding: '20px 24px',
                  marginTop: 16,
                  position: 'relative',
                }}
              >
                {/* Close button */}
                <button
                  onClick={() => setExpandedType(null)}
                  style={{
                    position: 'absolute',
                    top: 14,
                    right: 16,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: TEXT_SECONDARY,
                    fontSize: 18,
                    lineHeight: 1,
                    padding: 4,
                    opacity: 0.7,
                  }}
                  aria-label="Close explanation"
                  className="hover:opacity-100 transition-opacity"
                >
                  ✕
                </button>

                <div className="flex items-center gap-3 mb-3">
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 11,
                      backgroundColor: `${activeType.accent}18`,
                      border: `1px solid ${activeType.accent}40`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <DiveTypeIcon type={activeType.key} size={20} color={activeType.accent} strokeWidth={1.8} />
                  </div>
                  <div>
                    <p style={{ color: activeType.accent, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }} className="mb-0.5">
                      Dive Type Guide
                    </p>
                    {/* Dynamic title: "Wreck Diving in Larnaca" or just "Wreck Diving" */}
                    <h3 style={{ color: TEXT_PRIMARY, fontSize: 16, fontWeight: 700, lineHeight: 1.2 }}>
                      {guideTitle}
                    </h3>
                  </div>
                </div>

                {/* Contextual detail: region+type combo if available, else generic */}
                <p style={{ color: TEXT_SECONDARY, fontSize: 13.5, lineHeight: 1.7 }} className="mb-4">
                  {filters.region !== 'all' && REGION_TYPE_DESCRIPTIONS[filters.region]?.[activeType.key]
                    ? REGION_TYPE_DESCRIPTIONS[filters.region][activeType.key]
                    : activeType.detail}
                </p>

                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => {
                      setFilters((p) => ({ ...p, diveType: activeType.key }));
                      setExpandedType(null);
                      document.getElementById('dive-sites-grid')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    style={{
                      backgroundColor: `${activeType.accent}20`,
                      color: activeType.accent,
                      border: `1px solid ${activeType.accent}50`,
                      fontSize: 12,
                      fontWeight: 700,
                      padding: '7px 16px',
                      borderRadius: 20,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                    className="hover:opacity-80 transition-opacity"
                  >
                    <Icon name="FunnelIcon" size={12} style={{ color: activeType.accent }} />
                    Show {activeType.label} sites ↓
                  </button>
                  <button
                    onClick={() => setExpandedType(null)}
                    style={{
                      backgroundColor: 'transparent',
                      color: TEXT_SECONDARY,
                      border: `1px solid rgba(111,211,224,0.18)`,
                      fontSize: 12,
                      fontWeight: 600,
                      padding: '7px 16px',
                      borderRadius: 20,
                      cursor: 'pointer',
                    }}
                    className="hover:opacity-80 transition-opacity"
                  >
                    Close
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Helper hint */}
          <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
            <p style={{ color: TEXT_SECONDARY, fontSize: 12 }} className="flex items-center gap-1.5">
              <Icon name="InformationCircleIcon" size={13} style={{ color: CYAN, opacity: 0.7 }} />
              Tap a card to learn more about each dive type.
            </p>
            <Link
              href="/cyprus-diving-information"
              style={{ color: ORANGE, fontSize: 13, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
              className="hover:opacity-80 transition-opacity"
            >
              Full diving guide →
            </Link>
          </div>
        </div>
      </section>

      {/* ── DIVE REGIONS IN CYPRUS ────────────────────────────────────────── */}
      <section className="py-14">
        <div className="container mx-auto px-4">
          <div className="mb-8">
            <h2 style={{ color: TEXT_PRIMARY }} className="text-2xl md:text-3xl font-headline font-bold mb-2">
              Dive Regions in Cyprus
            </h2>
            <p style={{ color: TEXT_SECONDARY }} className="text-sm">
              Each region offers its own character — from the legendary Zenobia wreck in Larnaca to pristine walls in Akamas. Explore dedicated regional guides with full site listings and conditions.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {REGIONS.map((region) => {
              const count = diveSites.filter((s) => s.region === region.id).length;
              const highlights = REGION_HIGHLIGHTS[region.id] ?? [];
              return (
                <Link
                  key={region.id}
                  href={region.href}
                  style={{ backgroundColor: BG_CARD, border: `1px solid ${BORDER}` }}
                  className="group rounded-2xl p-5 hover:border-cyan-400/40 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 block"
                >
                  <div className="text-3xl mb-3">{region.icon}</div>
                  <h3
                    style={{ color: TEXT_PRIMARY }}
                    className="font-headline font-bold text-base mb-1 group-hover:text-cyan-300 transition-colors"
                  >
                    {region.label}
                  </h3>
                  <p style={{ color: TEXT_SECONDARY, fontSize: 12 }} className="mb-3 leading-snug">
                    {region.tagline}
                  </p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {highlights.slice(0, 3).map((h) => (
                      <span
                        key={h}
                        style={{
                          backgroundColor: 'rgba(111,211,224,0.07)',
                          color: CYAN,
                          fontSize: 10,
                          padding: '1px 6px',
                          borderRadius: 3,
                        }}
                      >
                        {h}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <span style={{ color: CYAN, fontSize: 12, fontWeight: 700 }}>{count} sites</span>
                    <Icon
                      name="ArrowRightIcon"
                      size={14}
                      style={{ color: CYAN }}
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── BROWSE DIVE SITES IN CYPRUS (Grid) ───────────────────────────── */}
      <section id="dive-sites-grid" style={{ backgroundColor: BG_SECTION, borderTop: `1px solid ${BORDER}` }} className="py-14">
        <div className="container mx-auto px-4">

          {/* Back to filters link */}
          <div className="flex justify-end mb-4">
            <a
              href="#dive-sites-filter"
              style={{ color: CYAN, fontSize: 13, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}
              className="hover:opacity-80 transition-opacity"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6"/></svg>
              Back to filters
            </a>
          </div>

          <div className="mb-8">
            <h2 style={{ color: TEXT_PRIMARY }} className="text-2xl md:text-3xl font-headline font-bold mb-2">
              {filters.region !== 'all'
                ? `Dive Sites in ${selectedRegionLabel}`
                : filters.diveType !== 'all'
                ? `${filters.diveType} Dive Sites in Cyprus`
                : 'Browse Dive Sites in Cyprus'}
            </h2>
            <p style={{ color: TEXT_SECONDARY }} className="text-sm">
              Use the filters above to find the right scuba diving site in Cyprus by region, type, access and experience level.
            </p>
          </div>

          {/* ── Results ── */}
          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <Icon name="MagnifyingGlassIcon" size={48} className="mx-auto mb-4" style={{ color: '#4A7A8A' }} />
              <h3 style={{ color: TEXT_PRIMARY }} className="text-xl font-headline font-bold mb-2">
                No dive sites match your filters
              </h3>
              <p style={{ color: TEXT_SECONDARY }} className="mb-6">
                Try adjusting or resetting your filters to find Cyprus dive sites.
              </p>
              <button
                onClick={handleReset}
                style={{ backgroundColor: CYAN, color: BG_DARK }}
                className="px-6 py-2 rounded-lg font-semibold hover:opacity-90 transition-opacity"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <>
              {/* Results count label — "Showing X dive sites in [Region]" */}
              <div
                style={{
                  backgroundColor: 'rgba(111,211,224,0.05)',
                  border: `1px solid ${BORDER}`,
                  borderRadius: 10,
                  padding: '10px 16px',
                  marginBottom: 24,
                }}
              >
                <p style={{ color: TEXT_SECONDARY, fontSize: 13 }}>
                  <Icon name="MapPinIcon" size={13} style={{ color: CYAN, display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
                  {getResultsLabel()}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((site) => (
                  <SiteIconCard key={site.id} site={site} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* ── EXPLORE DIVING IN OTHER REGIONS ──────────────────────────────── */}
      <section
        style={{ backgroundColor: BG_DARK, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}
        className="py-12"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <p style={{ color: TEXT_SECONDARY, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }} className="mb-2">
              Dive Planning
            </p>
            <h2 style={{ color: TEXT_PRIMARY }} className="text-xl md:text-2xl font-headline font-bold mb-2">
              Explore diving in other regions
            </h2>
            <p style={{ color: TEXT_SECONDARY, fontSize: 14 }} className="max-w-xl mx-auto">
              Each region of Cyprus offers a distinct diving experience. Select a region below to filter dive sites and explore what&apos;s available.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {REGION_CHIPS.map((chip) => {
              const isActive = filters.region === chip.id;
              const count = diveSites.filter((s) => s.region === chip.id).length;
              return (
                <button
                  key={chip.id}
                  onClick={() => {
                    setFilters((p) => ({ ...p, region: isActive ? 'all' : chip.id }));
                    document.getElementById('dive-sites-grid')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  style={{
                    backgroundColor: isActive ? `${CYAN}22` : BG_CARD,
                    color: isActive ? CYAN : TEXT_SECONDARY,
                    border: `1.5px solid ${isActive ? CYAN + '60' : BORDER}`,
                    fontSize: 13,
                    fontWeight: 600,
                    padding: '10px 20px',
                    borderRadius: 24,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    transition: 'all 0.15s',
                  }}
                  className="hover:border-cyan-400/50 hover:text-cyan-300 transition-all"
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>{REGION_CHIP_ICONS[chip.id]}</span>
                  <span>{chip.label}</span>
                  <span
                    style={{
                      backgroundColor: isActive ? `${CYAN}30` : 'rgba(111,211,224,0.08)',
                      color: isActive ? CYAN : TEXT_SECONDARY,
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '1px 7px',
                      borderRadius: 10,
                    }}
                  >
                    {count} sites
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── SEO SECTION ───────────────────────────────────────────────────── */}
      <section
        style={{ backgroundColor: BG_DARK, borderBottom: `1px solid ${BORDER}` }}
        className="py-14"
      >
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 style={{ color: TEXT_PRIMARY }} className="text-2xl md:text-3xl font-headline font-bold mb-5">
            Best Dive Sites in Cyprus for Scuba Diving
          </h2>
          <div style={{ color: TEXT_SECONDARY, fontSize: 14, lineHeight: 1.8 }} className="space-y-4">
            <p>
              Cyprus is one of the Mediterranean&apos;s premier scuba diving destinations, offering an exceptional variety of dive sites across five distinct regions. From the world-famous{' '}
              <Link href="/dive-sites/larnaca" style={{ color: CYAN, textDecoration: 'underline' }}>
                MS Zenobia wreck in Larnaca
              </Link>{' '}
              — consistently ranked among the top 10 wreck dives in the world — to the dramatic walls and caves of{' '}
              <Link href="/dive-sites/limassol" style={{ color: CYAN, textDecoration: 'underline' }}>
                Jubilee Shoals in Limassol
              </Link>
              , the island caters to every level of diver.
            </p>
            <p>
              Beginners will find ideal conditions at{' '}
              <Link href="/dive-sites/ayia-napa-protaras" style={{ color: CYAN, textDecoration: 'underline' }}>
                Green Bay in Ayia Napa & Protaras
              </Link>
              , a sheltered shore dive with underwater sculptures and frequent sea turtle sightings. Intermediate and advanced divers can explore reef systems, sea caves and artificial wrecks across{' '}
              <Link href="/dive-sites/paphos" style={{ color: CYAN, textDecoration: 'underline' }}>
                Paphos
              </Link>{' '}
              and{' '}
              <Link href="/dive-sites/akamas-latchi" style={{ color: CYAN, textDecoration: 'underline' }}>
                Akamas
              </Link>
              , where visibility regularly exceeds 20 metres.
            </p>
            <p>
              The warm, clear waters of Cyprus support a rich variety of{' '}
              <Link href="/marine-life" style={{ color: CYAN, textDecoration: 'underline' }}>
                Mediterranean marine life
              </Link>
              , including octopus, moray eels, grouper, amberjack, sea bream and occasional loggerhead sea turtles. Whether you&apos;re planning your first open water dive or a technical deep dive on the Zenobia, Cyprus has a dive site to match.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              {[
                { label: 'Larnaca Dive Sites', href: '/dive-sites/larnaca' },
                { label: 'Limassol Dive Sites', href: '/dive-sites/limassol' },
                { label: 'Paphos Dive Sites', href: '/dive-sites/paphos' },
                { label: 'Ayia Napa & Protaras Dive Sites', href: '/dive-sites/ayia-napa-protaras' },
                { label: 'Akamas & Latchi Dive Sites', href: '/dive-sites/akamas-latchi' },
                { label: 'Marine Life Guide', href: '/marine-life' },
              ].map(({ label, href }) => (
                <Link
                  key={href}
                  href={href}
                  style={{
                    backgroundColor: `${CYAN}10`,
                    color: CYAN,
                    border: `1px solid ${CYAN}30`,
                    fontSize: 12,
                    fontWeight: 600,
                    padding: '5px 12px',
                    borderRadius: 20,
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                  className="hover:bg-cyan-400/20 transition-colors"
                >
                  <Icon name="ArrowRightIcon" size={11} style={{ color: CYAN }} />
                  {label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────── */}
      <section
        style={{ backgroundColor: BG_SECTION, borderTop: `1px solid ${BORDER}` }}
        className="py-14"
      >
        <div className="container mx-auto px-4 text-center">
          <h2 style={{ color: TEXT_PRIMARY }} className="text-2xl md:text-3xl font-headline font-bold mb-4">
            Ready to Explore Cyprus Underwater?
          </h2>
          <p style={{ color: TEXT_SECONDARY }} className="mb-8 max-w-2xl mx-auto">
            Find certified dive centres across Cyprus, discover Mediterranean marine life, and plan your perfect scuba diving adventure.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              href="/dive-centres-directory"
              style={{ backgroundColor: CYAN, color: BG_DARK, border: `1.5px solid ${CYAN}` }}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold transition-all duration-200 hover:opacity-90"
            >
              <Icon name="BuildingStorefrontIcon" size={20} />
              <span>Find Dive Centres</span>
            </Link>
            <Link
              href="/marine-life"
              style={{
                backgroundColor: 'transparent',
                color: CYAN,
                border: `1.5px solid ${CYAN}`,
              }}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold transition-all duration-200 hover:bg-cyan-400/10"
            >
              <Icon name="SparklesIcon" size={20} />
              <span>Discover Marine Life</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DiveSitesMasterInteractive;
